import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Intent } from '@blueprintjs/core';

import { UserAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { Breadcrumbs } from 'components/navigation';
import { FormLocationSelector } from 'components/form-fields';
import { FormDragAndDropFileUploader, FormFileUploadStatus } from 'components/form-fields/FormDragAndDropFileUploader';
import { NotificationToaster, AlertUnsavedData, AlertConfirm } from 'components/notifications';
import { Button, ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { LocationService, EmployeeService, AssetService } from 'services';
import { recogniseTachoFile, TachoFileKind } from 'helpers';

// Tacho files have no registered MIME type, so the uploader matches them by extension.
const allowedFileTypes = ['.crd', '.dc', '.ddd', '.esm', '.up', '.vu'];
const maxFileSizeMegabytes = 15;
const maxFileSize = maxFileSizeMegabytes * 1024 * 1024;
const requiredAction = ['DCVU:Import'];

// Only the start of a file is needed to recognise it — driver cards are small and a
// vehicle unit's registration and date sit in its early sections. Reading a bounded
// slice keeps the synchronous recognition fast and the browser responsive for large
// files; anything not found within the slice falls back to a "-" description.
const recognitionByteLimit = 2 * 1024 * 1024;

const defaultArray = [];

// The tacho location list is keyed by the "Tacho" business area, the same area the
// Employee and Asset upload endpoints validate the supplied location against.
const tachoBusinessAreas = ['Tacho'];

// A file is added to the list before its recognition runs, so it starts with no kind.
const emptyRecognition = { kind: null, name: null, registration: null, date: null };

// The list shows driver cards first, then vehicle units, ordering by file name within
// each type. A file still being recognised has no kind yet, so it sorts last.
const tachoTypeOrder = { [TachoFileKind.CARD]: 0, [TachoFileKind.VU]: 1 };

function getTypeRank(kind) {
    const rank = tachoTypeOrder[kind];
    return rank == null ? 2 : rank;
}

function tachoDocumentComparator(documentA, documentB) {
    const typeDifference = getTypeRank(documentA.kind) - getTypeRank(documentB.kind);
    if (typeDifference !== 0) {
        return typeDifference;
    }
    return documentA.name.localeCompare(documentB.name);
}

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatTachoDate(date) {
    if (date == null || Number.isNaN(date.getTime())) {
        return null;
    }

    return `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

// Builds the per-file description shown in the upload list, e.g.
// "John Smith / Manchester / 28 May 2026" for a driver card or
// "AB12 CDE / Manchester / 30 May 2026" for a vehicle unit. Falls back to "-" when the
// file's own details could not be read.
function formatTachoFileDescription(recognition, locationName) {
    if (recognition == null || recognition.kind == null) {
        return '-';
    }

    const primary = recognition.kind === TachoFileKind.CARD ? recognition.name : recognition.registration;
    if (primary == null || primary.length === 0) {
        return '-';
    }

    const parts = [primary];
    if (locationName != null && locationName.length > 0) {
        parts.push(locationName);
    }

    const formattedDate = formatTachoDate(recognition.date);
    if (formattedDate != null) {
        parts.push(formattedDate);
    }

    return parts.join(" / ");
}

function buildLocationNameLookup(items, lookup) {
    (items ?? []).forEach((item) => {
        if (item.type === 'item') {
            lookup.set(item.id, item.name);
        } else {
            buildLocationNameLookup(item.items, lookup);
        }
    });
    return lookup;
}

function getLocationName(customLocations, locationId) {
    if (customLocations == null || locationId == null) {
        return '';
    }

    const lookup = buildLocationNameLookup(customLocations.location, new Map());
    return lookup.get(locationId) ?? '';
}

export function TachoImport(props) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customLocations, setCustomLocations] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState(defaultArray);
    const [useHierarchy, setUseHierarchy] = useState(false);
    const [files, setFiles] = useState(defaultArray);
    const [showClearConfirmation, setShowClearConfirmation] = useState(false);

    const currentLocationId = selectedLocations.length > 0 ? selectedLocations[0] : null;
    const currentLocationName = getLocationName(customLocations, currentLocationId);
    const locationSelected = currentLocationId != null;
    const hasPendingUploads = files.some((file) => file.status !== FormFileUploadStatus.COMPLETE);
    const hasUploadableFiles = files.some((file) => file.status !== FormFileUploadStatus.COMPLETE && file.recognition.kind != null);
    const isRecognising = files.some((file) => file.recognising);

    useEffect(initialLoad, []);

    function initialLoad() {
        LocationService.getFormLocationSelectorCustomLocations(tachoBusinessAreas).then(
            (locationData) => {
                setCustomLocations(locationData);
                setLoading(false);
            },
            () => {
                NotificationToaster.show(Intent.DANGER, 'Could not load the upload locations. Please refresh the page.');
                setLoading(false);
            }
        );
    }

    async function recogniseFile(file) {
        try {
            const buffer = await file.slice(0, recognitionByteLimit).arrayBuffer();
            return recogniseTachoFile(buffer);
        } catch {
            return emptyRecognition;
        }
    }

    // FormLocationSelector toggles the chosen id into an array; in single-select mode
    // keep only the most recent pick so choosing a different location replaces the
    // previous one rather than leaving two selected (which the selector shows as blank).
    // The uploader is disabled until a location is chosen, so each file captures the
    // location selected when it was queued and keeps it even if the dropdown changes.
    function onLocationChange(locationIds) {
        setSelectedLocations(locationIds.length > 0 ? [locationIds[locationIds.length - 1]] : defaultArray);
    }

    function onFilesSelected(newFiles) {
        // Add the files straight away with a placeholder description so a large file's
        // recognition never blocks the UI; recognition runs in the background and patches
        // each row (or drops it if the file turns out not to be a tacho file) when it
        // returns.
        const queuedFiles = newFiles.map((file) => ({
            file: file,
            recognition: emptyRecognition,
            recognising: true,
            status: FormFileUploadStatus.PENDING,
            statusComment: null,
            locationId: currentLocationId,
            locationName: currentLocationName
        }));
        setFiles((previousFiles) => [...previousFiles, ...queuedFiles]);

        newFiles.forEach((file) => {
            recogniseFile(file).then((recognition) => {
                if (recognition.kind == null) {
                    setFiles((previousFiles) => previousFiles.filter((queued) => queued.file !== file));
                    NotificationToaster.show(Intent.WARNING, `"${file.name}" was not added because it is not a recognised tachograph file.`);
                } else {
                    setFiles((previousFiles) => previousFiles.map((queued) => {
                        if (queued.file === file) {
                            return { ...queued, recognition: recognition, recognising: false };
                        }
                        return queued;
                    }));
                }
            });
        });
    }

    function removeFile(fileName) {
        setFiles((previousFiles) => previousFiles.filter((file) => file.file.name !== fileName));
    }

    function onClearQueue() {
        // Clearing discards any file that has not yet uploaded, so confirm first when the
        // queue still holds pending work; a queue of only-completed files clears straight
        // away as there is nothing to lose.
        if (hasPendingUploads) {
            setShowClearConfirmation(true);
        } else {
            clearQueue();
        }
    }

    function clearQueue() {
        setFiles(defaultArray);
        setShowClearConfirmation(false);
    }

    function updateFileStatus(fileName, status, statusComment) {
        setFiles((previousFiles) => previousFiles.map((file) => {
            if (file.file.name === fileName) {
                return { ...file, status: status, statusComment: statusComment };
            }
            return file;
        }));
    }

    function uploadFile(file) {
        // Each file is uploaded to the microservice that owns it: driver cards to
        // Employee, vehicle units to Asset, each with the selected location.
        const request = file.recognition.kind === TachoFileKind.CARD
            ? EmployeeService.uploadDriverCardFile(file.file, file.locationId)
            : AssetService.uploadVehicleUnitFile(file.file, file.locationId);

        return request.then(
            () => {
                updateFileStatus(file.file.name, FormFileUploadStatus.COMPLETE, null);
                return true;
            },
            (error) => {
                updateFileStatus(file.file.name, FormFileUploadStatus.FAILED, typeof error === 'string' ? error : 'The file could not be uploaded.');
                return false;
            }
        );
    }

    function onUpload() {
        // Only recognised files with a location assigned are uploaded; each goes to its
        // own assigned location.
        const filesToUpload = files.filter((file) => file.status !== FormFileUploadStatus.COMPLETE && file.recognition.kind != null && file.locationId != null);
        if (filesToUpload.length === 0) {
            return;
        }

        setSaving(true);
        const uploadingNames = new Set(filesToUpload.map((file) => file.file.name));
        setFiles((previousFiles) => previousFiles.map((file) => {
            if (uploadingNames.has(file.file.name)) {
                return { ...file, status: FormFileUploadStatus.UPLOADING, statusComment: null };
            }
            return file;
        }));

        // Each row resolves independently (uploadFile never rejects) so one failure does
        // not block the others; the result toast reports how many made it.
        Promise.all(filesToUpload.map(uploadFile)).then((results) => {
            setSaving(false);

            const uploadedCount = results.filter(Boolean).length;
            const totalCount = results.length;

            if (uploadedCount === totalCount) {
                NotificationToaster.show(Intent.SUCCESS, 'All files uploaded successfully.');
            } else if (uploadedCount === 0) {
                NotificationToaster.show(Intent.DANGER, 'None of the files could be uploaded.');
            } else {
                NotificationToaster.show(Intent.WARNING, `Only ${uploadedCount}/${totalCount} files were uploaded.`);
            }
        });
    }

    const documents = files.map((file) => {
        const fileKind = file.recognition.kind;
        const isDriverCard = fileKind === TachoFileKind.CARD;
        const description = formatTachoFileDescription(file.recognition, file.locationName);

        return {
            name: file.file.name,
            kind: fileKind,
            size: file.file.size,
            // A leading icon lets the user tell a driver card from a vehicle unit at a
            // glance; it appears once the file has been recognised.
            version: (
                <span className="inline-items">
                    <ShowHide
                        evaluator={fileKind != null}
                        show={(
                            <span className="inline-item">
                                <i className={classNames('fa', isDriverCard ? 'fa-user' : 'fa-truck')} title={isDriverCard ? 'Driver card' : 'Vehicle unit'} />
                            </span>
                        )}
                    />
                    <span className="inline-item">{description}</span>
                </span>
            ),
            status: file.status,
            statusComment: file.statusComment
        };
    });

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={props.breadcrumbs} />

                    <h2>Tacho Files</h2>
                    <div className="intro-text">
                        <p>Upload driver card and vehicle unit tachograph files for analysis.</p>
                    </div>

                    <FormLocationSelector
                        businessArea=""
                        useCustomLocations={true}
                        customLocations={customLocations}
                        selectedLocations={selectedLocations}
                        setSelectedLocations={onLocationChange}
                        useHierarchy={useHierarchy}
                        setUseHierarchy={setUseHierarchy}
                        singleSelection={true}
                        headingText="Upload location:"
                        loading={loading}
                        disabled={saving}
                    />

                    <p className="helper-text">Accepted file types: .crd, .dc, .ddd, .esm, .up, .vu. Maximum 15 MB per file.</p>

                    <FormDragAndDropFileUploader
                        onChange={onFilesSelected}
                        disabled={loading || saving || !locationSelected}
                        loading={loading}
                        documents={documents}
                        onRemoveDocument={removeFile}
                        maxFileSize={maxFileSize}
                        allowedFileTypes={allowedFileTypes}
                        showVersions={true}
                        versionColumnHeader="Description"
                        sortComparator={tachoDocumentComparator}
                        fixedTableHeight={false}
                    />

                    <div className="button-row">
                        <ButtonSave
                            text="Upload"
                            icon="upload"
                            onClick={onUpload}
                            loading={loading}
                            disabled={saving}
                            simpleDisabled={!hasUploadableFiles || isRecognising}
                        />
                        <ShowHide
                            evaluator={files.length > 0}
                            show={(
                                <Button
                                    text="Clear queue"
                                    intent="secondary"
                                    onClick={onClearQueue}
                                    disabled={saving}
                                />
                            )}
                        />
                    </div>

                    <AlertConfirm
                        isOpen={showClearConfirmation}
                        title="Clear queue"
                        confirmButtonText="Clear queue"
                        onConfirm={clearQueue}
                        onCancel={() => setShowClearConfirmation(false)}
                    >
                        <p>Some files have not been uploaded yet. Clearing the queue will discard them. Are you sure you want to continue?</p>
                    </AlertConfirm>

                    <AlertUnsavedData isDirty={hasPendingUploads && !saving} />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

TachoImport.propTypes = {
    breadcrumbs: PropTypes.array
};
